import type { FunctionComponent } from 'react';
import { useId, useMemo } from 'react';

import styles from './Chip3d.module.css';

const MathPi180 = Math.PI / 180;
const segmentAngle = 25.6 * MathPi180;
const chipRadius = 50;
const lineOffsetAngles = [0, 58, -58, 180, 122, -122];

export interface ChipDepthProps {
  rotate: number;
  tiltComponents: {
    tiltSin: number;
    tiltCos: number;
  };
  isFreeChip: boolean;
}

export const ChipDepth: FunctionComponent<ChipDepthProps> = ({ rotate, tiltComponents, isFreeChip }) => {
  const lines: { start: number; end: number; startAngle: number; endAngle: number; hidden: boolean }[] = useMemo(() => {
    return lineOffsetAngles.map((lineOffsetAngle) => {
      const lineOffset = (rotate + lineOffsetAngle) * MathPi180;

      const constrainAngle = (angle: number): number => {
        angle = angle % (2 * Math.PI);
        if (angle > Math.PI) {
          angle -= 2 * Math.PI;
        }
        if (angle < -Math.PI) {
          angle += 2 * Math.PI;
        }
        return angle;
      };

      const lineStartAngle = constrainAngle(lineOffset + segmentAngle / 2);
      const lineEndAngle = constrainAngle(lineOffset - segmentAngle / 2);
      const line = {
        start: 0,
        startAngle: lineStartAngle,
        end: 0,
        endAngle: lineEndAngle,
        hidden: false,
      };

      const preventBackfaceVisibility = (point: number, angle: number): { point: number; isHidden: boolean } => {
        const result = { point, isHidden: false };
        if (angle < -Math.PI / 2) {
          result.point = chipRadius * 2 + (chipRadius * 2 - point);
          result.isHidden = true;
        } else if (angle > Math.PI / 2) {
          result.point = -point;
          result.isHidden = true;
        }
        return result;
      };

      const { point: lineStart, isHidden: startHidden } = preventBackfaceVisibility(
        chipRadius - Math.sin(lineStartAngle) * chipRadius,
        lineStartAngle
      );
      line.start = lineStart;

      const { point: lineEnd, isHidden: endHidden } = preventBackfaceVisibility(
        chipRadius - Math.sin(lineEndAngle) * chipRadius,
        lineEndAngle
      );
      line.end = lineEnd;

      if (startHidden && endHidden) {
        line.hidden = true;
      }
      return line;
    });
  }, [rotate]);
  const gradientId = useId();
  const maskId = useId();

  return (
    <span className={styles.chip__depthContainer}>
      <span className={styles.chip__depth}>
        <span className={styles.chip__depthThickness}>
          <svg width="0" height="0" display="none">
            <defs>
              <linearGradient id={gradientId} gradientTransform="rotate(147.96)">
                <stop offset="10.75%" stopColor="#FFA806" />
                <stop offset="28.37%" stopColor="#FEB321" />
                <stop offset="47.38%" stopColor="#FFBB00" />
                <stop offset="62.54%" stopColor="#D98A00" />
                <stop offset="70.02%" stopColor="#FFA100" />
                <stop offset="81.02%" stopColor="#FFBF30" />
              </linearGradient>
            </defs>
          </svg>
          <svg width="100" height="10.5" viewBox="0 0 100 10.5" xmlns="http://www.w3.org/2000/svg">
            <rect width="100" height="10.5" fill={isFreeChip ? `url(#${gradientId})` : 'var(--chipBackground)'} />
            {lines.map((line) =>
              !line.hidden ? (
                <path
                  key={`${line.start}${line.end}`}
                  d={`M${line.start} 0H${line.end}V50H${line.start}V0Z`}
                  fill={isFreeChip ? '#040301' : 'white'}
                  fillOpacity="0.8"
                />
              ) : null
            )}
            <rect width="100" height="50" fill="#000000" fillOpacity={0.2 + 0.2 * tiltComponents.tiltSin} />
          </svg>
        </span>
        <span className={styles.chip__depthBottom}>
          <svg width="100" height="50" viewBox="0 0 100 50" fill="none" xmlns="http://www.w3.org/2000/svg">
            <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="100" height="50">
              <path d="M100 0C100 27.6142 77.6142 50 50 50C22.3858 50 0 27.6142 0 0H100Z" fill="white" />
            </mask>
            <g mask={`url(#${maskId})`}>
              <rect width="100" height="50" fill={isFreeChip ? `url(#${gradientId})` : 'var(--chipBackground)'} />
              {lines
                .filter((line) => !line.hidden)
                .map((line) => {
                  return (
                    <path
                      key={`${line.start}${line.end}`}
                      d={`M${line.start} 0H${line.end}V50H${line.start}V0Z`}
                      fill={isFreeChip ? '#040301' : 'white'}
                      fillOpacity="0.8"
                    />
                  );
                })}

              <rect width="100" height="50" fill="#000000" fillOpacity={0.2 + 0.2 * tiltComponents.tiltSin} />
            </g>
          </svg>
        </span>
      </span>
    </span>
  );
};
